/**
 * Tastenkuerzel der Regie.
 *
 * Zwei Sorten: Die Notfall-Schalter mit Strg wirken immer, auch beim Tippen
 * im Chat -- wenn im Hintergrund etwas gesagt wird, das nicht raus soll,
 * sucht niemand erst den Knopf. Alles andere wirkt nur ausserhalb von
 * Textfeldern, und was das Bild umschaltet, braucht zwei Zusatztasten: ein
 * Schnitt soll nicht aus Versehen passieren.
 *
 * Am Mac ist es die ctrl-Taste, nicht ⌘: ⌘M legt das Fenster ins Dock, ⌘K
 * und ⌘B belegt der Browser. ctrl ist dort frei und heisst auf beiden
 * Systemen gleich -- „Steuerung".
 */

export const IS_MAC =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

export type ShortcutId =
  | 'mic'
  | 'cam'
  | 'mute'
  | 'blank'
  | 'take'
  | 'second'
  | 'secondOff'
  | 'swap'
  | 'course'
  | 'lshape'
  | 'talk'
  | 'monitor'
  | 'chat'
  | 'requests'
  | 'help';

export type ShortcutGroup = 'notfall' | 'schnitt' | 'ansicht';

type Part = 'ctrl' | 'shift' | 'alt' | string;

/** Platzhalter fuer die Nummer der Quelle -- im Tooltip steht dort die echte. */
const SLOT = '1–9';

export const SHORTCUTS: { id: ShortcutId; parts: Part[]; label: string; group: ShortcutGroup }[] = [
  { id: 'mic', parts: ['ctrl', 'M'], label: 'Dein Mikro an/aus', group: 'notfall' },
  { id: 'cam', parts: ['ctrl', 'K'], label: 'Bild deiner Kamera an/aus', group: 'notfall' },
  { id: 'mute', parts: ['ctrl', 'S'], label: 'Ausgang stumm („Ton aus“) an/aus', group: 'notfall' },
  { id: 'blank', parts: ['ctrl', 'B'], label: 'Abblenden an/aus — im Stream läuft das Standardbild', group: 'notfall' },
  { id: 'take', parts: ['ctrl', 'shift', SLOT], label: 'Quelle 1–9 ins Hauptbild', group: 'schnitt' },
  { id: 'second', parts: ['ctrl', 'alt', SLOT], label: 'Quelle 1–9 klein bzw. daneben (Splitscreen)', group: 'schnitt' },
  { id: 'secondOff', parts: ['ctrl', 'alt', '0'], label: 'Kleines Fenster bzw. Splitscreen aus', group: 'schnitt' },
  { id: 'swap', parts: ['ctrl', 'alt', 'T'], label: 'Im Splitscreen die Seiten tauschen', group: 'schnitt' },
  { id: 'course', parts: ['ctrl', 'alt', 'S'], label: 'Strecke im Vollbild an/aus (Backyard)', group: 'schnitt' },
  { id: 'lshape', parts: ['ctrl', 'alt', 'L'], label: 'L-Form an/aus — mit Dauer und Auswahl aus dem Werbe-Block', group: 'schnitt' },
  { id: 'talk', parts: ['alt', SLOT], label: 'Vorgespräch mit Quelle 1–9 an/aus', group: 'schnitt' },
  { id: 'monitor', parts: ['H'], label: 'Hören an/aus', group: 'ansicht' },
  { id: 'chat', parts: ['C'], label: 'Chat ein-/ausklappen (am Handy: Reiter Chat)', group: 'ansicht' },
  { id: 'requests', parts: ['F'], label: 'Freigaben ein-/ausklappen (am Handy: Reiter Freigaben)', group: 'ansicht' },
  { id: 'help', parts: ['?'], label: 'Hilfe auf/zu', group: 'ansicht' },
];

const MODIFIERS: Record<string, string> = IS_MAC
  ? { ctrl: 'ctrl', shift: '⇧', alt: '⌥' }
  : { ctrl: 'Strg', shift: 'Umschalt', alt: 'Alt' };

/** Die Tasten eines Kuerzels einzeln, so wie sie auf diesem Rechner heissen. */
export function keyParts(id: ShortcutId | 'escape', slot?: number): string[] {
  if (id === 'escape') return ['Esc'];
  const shortcut = SHORTCUTS.find((s) => s.id === id);
  if (!shortcut) return [];
  return shortcut.parts.map((p) => MODIFIERS[p] ?? (p === SLOT && slot !== undefined ? String(slot) : p));
}

/** „Strg+Umschalt+3" -- fuer Tooltips und die Bestaetigung. */
export const keyLabel = (id: ShortcutId, slot?: number) => keyParts(id, slot).join('+');

/** Nur Quellen mit Nummer 1 bis 9 haben ein Kuerzel. */
export const hasSlotKey = (slot: number | undefined): slot is number => slot !== undefined && slot >= 1 && slot <= 9;

export interface ShortcutHit {
  id: ShortcutId;
  /** Nummer der Quelle bei Hauptbild, klein und Vorgespraech. */
  slot?: number;
}

const DIGIT = /^(?:Digit|Numpad)([0-9])$/;
const LETTER = /^Key([A-Z])$/;

/** Tippt jemand gerade? Dann gehoeren Ziffern und Buchstaben dem Feld. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement) {
    return !['checkbox', 'radio', 'range', 'button', 'submit', 'reset', 'color', 'file'].includes(target.type);
  }
  return false;
}

/**
 * Welches Kuerzel ein Tastendruck ist -- oder keins.
 *
 * Ziffern und Buchstaben mit Zusatztaste werden ueber `code` gelesen, also
 * nach der Lage der Taste: ⌥ am Mac macht aus „T" ein „†", Umschalt aus „1"
 * ein „!". Ohne Zusatztaste zaehlt `key`, damit ein „C" auch auf einer
 * fremden Tastatur dort ist, wo es draufsteht.
 */
export function readShortcut(e: KeyboardEvent): ShortcutHit | null {
  if (e.metaKey || e.isComposing) return null;
  const digitMatch = DIGIT.exec(e.code);
  const digit = digitMatch ? Number(digitMatch[1]) : null;
  const letter = LETTER.exec(e.code)?.[1] ?? null;

  // Notfall-Schalter: wirken auch im Textfeld. Ohne Alt -- unter Windows ist
  // Strg+Alt die AltGr-Taste, und AltGr+M tippt ein „µ".
  if (e.ctrlKey && !e.altKey && !e.shiftKey) {
    const hit = { M: 'mic', K: 'cam', S: 'mute', B: 'blank' }[letter ?? ''] as ShortcutId | undefined;
    return hit ? { id: hit } : null;
  }

  // Alles Weitere gehoert im Textfeld dem Feld -- auch Strg+Alt+Ziffer, das
  // unter Windows mit deutscher Tastatur „{", „[" und „]" schreibt.
  if (isTyping(e.target)) return null;

  if (digit !== null) {
    if (e.ctrlKey && e.shiftKey && !e.altKey) return digit >= 1 ? { id: 'take', slot: digit } : null;
    if (e.ctrlKey && e.altKey && !e.shiftKey) return digit === 0 ? { id: 'secondOff' } : { id: 'second', slot: digit };
    if (e.altKey && !e.ctrlKey && !e.shiftKey) return digit >= 1 ? { id: 'talk', slot: digit } : null;
    return null;
  }
  if (e.ctrlKey && e.altKey && !e.shiftKey && letter === 'T') return { id: 'swap' };
  if (e.ctrlKey && e.altKey && !e.shiftKey && letter === 'S') return { id: 'course' };
  if (e.ctrlKey && e.altKey && !e.shiftKey && letter === 'L') return { id: 'lshape' };
  if (e.ctrlKey || e.altKey) return null;

  if (e.key === '?') return { id: 'help' };
  if (e.shiftKey) return null;
  const hit = { h: 'monitor', c: 'chat', f: 'requests' }[e.key.toLowerCase()] as ShortcutId | undefined;
  return hit ? { id: hit } : null;
}
